import { useState, type ReactElement } from "react";
import { Plus } from "lucide-react";
import {
  COUNTERMEASURE_TABLE_MAX_ROWS,
  type CountermeasureRow,
  type CountermeasureTableParams,
} from "@playbookai/widget-schemas";
import { Button } from "../../atoms/index.js";
import type { WidgetBodyProps } from "../types.js";
import { CountermeasureList } from "./Component.js";
import { CountermeasureDetails } from "./CountermeasureDetails.js";

export function CountermeasureExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<CountermeasureTableParams>): ReactElement {
  const [selectedId, setSelectedId] = useState<string>();
  const [newRow, setNewRow] = useState<CountermeasureRow>();
  const [dirty, setDirty] = useState(false);
  const [revision, setRevision] = useState(0);
  // Retain selection identity if removed remotely, never edit the next row by accident.
  const selected = selectedId
    ? data.rows.find((row) => row.id === selectedId)
    : data.rows[0];
  const reset = () => {
    setDirty(false);
    setNewRow(undefined);
    setRevision((value) => value + 1);
  };
  const add = () => {
    if (
      readOnly ||
      dirty ||
      newRow ||
      data.rows.length >= COUNTERMEASURE_TABLE_MAX_ROWS
    )
      return;
    setNewRow({
      id: crypto.randomUUID(),
      title: "",
      owner: "",
      window: "",
      impact: "Med",
      effort: "Med",
      status: "Proposed",
    });
  };
  return (
    <div className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-pb-text-muted">
          {dirty || newRow
            ? "Save or cancel before selecting another countermeasure."
            : "Select a countermeasure"}
        </p>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={add}
            disabled={
              dirty ||
              !!newRow ||
              data.rows.length >= COUNTERMEASURE_TABLE_MAX_ROWS
            }
          >
            <Plus className="me-2 h-4 w-4" />
            Add countermeasure
          </Button>
        )}
      </div>
      <div className="grid shrink-0 gap-5 @3xl:min-h-0 @3xl:flex-1 @3xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="flex min-w-0 flex-col @3xl:min-h-0">
          <CountermeasureList
            data={data}
            selectedId={newRow ? undefined : selected?.id}
            selectionLocked={dirty || !!newRow}
            onSelect={(id) => {
              setSelectedId(id);
              reset();
            }}
          />
          <p className="mt-4 text-xs text-pb-text-muted">
            {data.rows.length} countermeasure{data.rows.length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="min-w-0 border-t border-pb-border pt-4 @3xl:overflow-auto @3xl:border-s @3xl:border-t-0 @3xl:ps-5 @3xl:pt-0">
          {newRow || selected ? (
            <CountermeasureDetails
              key={(newRow?.id ?? selected?.id) + ":" + revision}
              row={(newRow ?? selected)!}
              data={data}
              isNew={!!newRow}
              readOnly={!!readOnly}
              onDirty={() => {
                if (selected) setSelectedId(selected.id);
                setDirty(true);
              }}
              onCancel={() => {
                reset();
                if (!selected) setSelectedId(undefined);
              }}
              onSave={(row, options) => {
                const next = {
                  ...data,
                  rows: newRow
                    ? [...data.rows, row]
                    : data.rows.map((item) =>
                        item.id === row.id ? row : item,
                      ),
                };
                const saved = options
                  ? onChange(next, options)
                  : onChange(next);
                const done = () => {
                  setSelectedId(row.id);
                  reset();
                };
                // A pick's save is awaited: a refusal keeps the draft open.
                if (options?.grantViewerIfMissing) {
                  return Promise.resolve(saved).then(done);
                }
                done();
                return undefined;
              }}
              onDelete={() => {
                if (readOnly) return;
                onChange({
                  ...data,
                  rows: data.rows.filter((row) => row.id !== selected?.id),
                });
                setSelectedId(undefined);
                reset();
              }}
            />
          ) : (
            <div className="space-y-3 text-sm text-pb-text-muted">
              <p>
                {selectedId
                  ? "This countermeasure was removed. Select another or add one."
                  : "Add a countermeasure to begin."}
              </p>
              {dirty && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    reset();
                    setSelectedId(undefined);
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
